import {
  Component, ChangeDetectionStrategy, Inject, Output, EventEmitter, Input, OnDestroy
} from '@angular/core';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { Airport } from '@app/modules/core/interfaces/airport';
import { FlightType } from '@search-bar/entities/flight-type';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { HomeItemDetailFacade } from '@app/modules/home-item-detail/home-item-detail.facade';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { AutocompletePlaces } from '@search-bar/entities/autocomplete-places';
import { SearchType } from '@search-bar/entities/search-type';
import { ChangeInputPayload } from '@shared/base-search-bar/entities/search-input-payload';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { BusType } from '@search-bar/entities/bus-type';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
import { TripTypes } from '@app/entities/trip-types';
import { AmplitudeEventParams } from '@amplitude/interfaces/amplitude-events';
import { HomeFlightOffers } from '@shared/home-sections/entities/home-data.response';
import { isNullOrUndefined } from '@app/util/util';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';

@Component({
  selector: 'app-search-bar-mobile',
  templateUrl: './search-bar-mobile.container.html',
  styleUrls: ['./search-bar-mobile.container.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class SearchBarMobileContainer implements OnDestroy {

  @Input() searchType: SearchType;
  @Input() airports: Airport[] = [];
  @Input() defaultPlace: SelectAutocompletePlace;
  @Input() currentUrl: SelectAutocompletePlace;
  @Input() searchData: SearchData;
  @Input() isExchangeSearch: boolean;
  @Input() departureDateIsDisabled: boolean;
  @Input() routeChangeIsDisabled: boolean;
  @Input() exchangeTripType: TripTypes;
  @Input() destination: boolean;
  @Input() validate: boolean;
  @Input() source: EVENT_SOURCE | string;
  @Input() baggageIncluded: boolean;

  @Output() emitArrangement: EventEmitter<PassengerArrangement> = new EventEmitter<PassengerArrangement>();
  @Output() selectCalendar: EventEmitter<Date[]> = new EventEmitter<Date[]>();
  @Output() emitFlightClassSelection: EventEmitter<FlightClass> = new EventEmitter<FlightClass>();
  @Output() placeInputChanged: EventEmitter<ChangeInputPayload> = new EventEmitter<ChangeInputPayload>();
  @Output() selectPlace: EventEmitter<SelectAutocompletePlace> = new EventEmitter<SelectAutocompletePlace>();
  @Output() selectFlightType: EventEmitter<FlightType> = new EventEmitter<FlightType>();
  @Output() selectBusType: EventEmitter<BusType> = new EventEmitter<BusType>();
  @Output() emitSearch: EventEmitter<void> = new EventEmitter<void>();
  @Output() swapLocationsEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Output() selectedBaggageIncluded: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() showMobileCalendarEvent: EventEmitter<CalendarField> = new EventEmitter<CalendarField>();
  @Output() amplitudeEvent: EventEmitter<AmplitudeEventParams> = new EventEmitter<AmplitudeEventParams>();
  @Output() toggleSearchByFlexDates: EventEmitter<void> = new EventEmitter<void>();
  @Output() toggleEvent: EventEmitter<void> = new EventEmitter<void>();

  private flightOffersSubscription: Subscription;

  constructor(
    private readonly facade: BaseSearchBarFacade,
    private readonly homeItemDetailFacade: HomeItemDetailFacade,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) {
    this.fetchFlightsOffersValues();
  }

  public ngOnDestroy(): void {
    if (this.flightOffersSubscription) {
      this.flightOffersSubscription.unsubscribe();
    }
  }

  get isResultsPage(): boolean {
    return this.searchType === SearchType.FLIGHT_RESULTS || this.searchType === SearchType.BUSES_RESULTS ||
      this.exchangeSearch;
  }

  get exchangeSearch(): boolean {
    return this.searchType === SearchType.FLIGHT_USER_HISTORY;
  }

  get isBuses(): boolean {
    return this.searchType === SearchType.BUSES_RESULTS || this.searchType === SearchType.HOME_BUSES;
  }

  get departurePlace$(): Observable<AutocompletePlace> {
    return this.facade.departurePlace$;
  }

  get departureTerminal$(): Observable<AutocompletePlace> {
    return this.facade.departureTerminal$;
  }

  get arrivalTerminal$(): Observable<AutocompletePlace> {
    return this.facade.arrivalTerminal$;
  }

  get arrivalPlace$(): Observable<AutocompletePlace> {
    return this.facade.arrivalPlace$;
  }

  get departureDate$(): Observable<Date> {
    return this.facade.departureDate$;
  }

  get arrivalDate$(): Observable<Date> {
    return this.facade.arrivalDate$;
  }

  get flightType$(): Observable<FlightType> {
    return this.facade.flightType$;
  }

  get busType$(): Observable<BusType> {
    return this.facade.flightType$.pipe(map(type => type.toString() as BusType));
  }

  get flightClass$(): Observable<FlightClass> {
    return this.facade.flightClass$;
  }

  get passengerArrangement$(): Observable<PassengerArrangement[]> {
    return this.facade.passengerArrangements$;
  }

  get autoCompletePlaces$(): Observable<AutocompletePlaces> {
    return this.facade.autoCompletePlaces$;
  }

  get isLoadingAutocompletePlaces$(): Observable<boolean> {
    return this.facade.isLoadingAutocompletePlaces$;
  }

  public fetchFlightsOffersValues(): void {
    this.flightOffersSubscription = combineLatest([
      this.departurePlace$,
      this.arrivalPlace$,
      this.flightType$
    ]).subscribe(([departurePlace, arrivalPlace, flightType]) => {
      if (!isNullOrUndefined(departurePlace.code) && !isNullOrUndefined(arrivalPlace.code) && 
          departurePlace.code !== arrivalPlace.code && !this.isExchangeSearch) {
        this.homeItemDetailFacade.fetchHomeFlightOffers(departurePlace.code, arrivalPlace.code, flightType);
      }
    });
  }

  get flightOffers$(): Observable<HomeFlightOffers[]> {
    return this.homeItemDetailFacade.flightOffers$;
  }

  public clearAutocompletePlaces(): void {
    this.facade.resetAutocomplete();
  }

}
